import React, { useState } from "react";
import { BadgeCheck, Banknote, HandCoins, Megaphone } from "lucide-react";
import { BoutonSombre, Eyebrow, Section } from "./ui";

/* Ce qui tourne tout seul : l'école doit pouvoir le lire sans ouvrir le code. */
export function Automatismes({ automatismes, manuel }) {
  return (
    <Section
      titre="Ce qui se fait tout seul"
      aide="Règle de la maison : si ça peut être automatique, ça l'est. Voici ce que la console décide sans intervention."
      testid="reglages-automatismes"
    >
      <div className="grid md:grid-cols-2 gap-3">
        {automatismes.map((a) => (
          <div key={a.regle} className="bg-white rounded-2xl p-4 border" style={{ borderColor: "var(--border)" }}
            data-testid={`automatisme-${a.regle.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <BadgeCheck size={15} style={{ color: "#15803D" }} /> {a.regle}
            </div>
            <p className="text-[11px] text-[var(--muted)] mt-1.5">{a.quoi}</p>
            <div className="text-[10px] uppercase tracking-[0.14em] mt-2" style={{ color: "var(--primary)" }}>{a.ou}</div>
          </div>
        ))}
      </div>
      <div className="rounded-2xl p-5" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }} data-testid="reglages-manuel">
        <div className="text-sm font-semibold flex items-center gap-2" style={{ color: "#B45309" }}>
          <HandCoins size={15} /> Les deux seules choses qui restent à la main
        </div>
        <ul className="mt-2 space-y-1 text-xs" style={{ color: "#92400E" }}>
          {manuel.map((m) => <li key={m}>· {m}</li>)}
        </ul>
      </div>
    </Section>
  );
}

/* Sur quel bulletin part la facture. */
export function Encaissement({ disciplines, onEnregistrer }) {
  const [txt, setTxt] = useState(disciplines.join(", "));
  const modifie = txt !== disciplines.join(", ");
  return (
    <Section titre="Encaissement" aide="Deux bulletins de versement : celui de l'école, et celui de Noémie Vaney pour les cours qu'elle encaisse." testid="reglages-encaissement">
      <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Banknote size={15} style={{ color: "var(--primary)" }} /> Disciplines encaissées par Noémie Vaney
        </div>
        <p className="text-[11px] text-[var(--muted)] mt-1.5">
          Dès qu'un cours de ces disciplines apparaît dans la facture d'une famille, <strong>toute la facture</strong>
          {" "}est établie au nom de Noémie : la famille ne reçoit qu'une seule facture, même si elle suit d'autres cours.
          Une note « BV Noémie » écrite à la main sur une ligne de facturation reste possible en exception.
        </p>
        <div className="flex gap-3 mt-3">
          <input className="input-field" style={{ maxWidth: 320 }} value={txt} onChange={(e) => setTxt(e.target.value)}
            data-testid="encaissement-disciplines" />
          <BoutonSombre
            onClick={() => onEnregistrer(txt.split(",").map((s) => s.trim()).filter(Boolean))}
            testid="encaissement-enregistrer"
            disabled={!modifie}
          >
            Enregistrer
          </BoutonSombre>
        </div>
      </div>
    </Section>
  );
}

/* « Comment avez-vous entendu parler de 1000 ARTS ? » — prêt, pas encore posé. */
export function Provenance({ sources, repartition, onEnregistrer }) {
  const [txt, setTxt] = useState(sources.join("\n"));
  const modifie = txt !== sources.join("\n");
  const total = repartition.reduce((s, r) => s + r.nb, 0);
  return (
    <Section
      titre="D'où viennent les inscriptions"
      aide="La question « Comment avez-vous entendu parler de 1000 ARTS ? » est prête ici. Elle n'est pas encore posée sur le site : dis-le-moi et je l'ajoute aux deux formulaires."
      testid="reglages-provenance"
    >
      <div className="grid md:grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Megaphone size={15} style={{ color: "var(--primary)" }} /> Les réponses proposées
          </div>
          <p className="text-[11px] text-[var(--muted)] mt-1.5">
            Une par ligne, dans l'ordre d'affichage. « Autre » ouvre un champ libre sur le formulaire.
          </p>
          <textarea className="input-field mt-3" rows={9} value={txt} onChange={(e) => setTxt(e.target.value)}
            data-testid="provenance-options" />
          <div className="mt-3">
            <BoutonSombre
              onClick={() => onEnregistrer(txt.split("\n").map((s) => s.trim()).filter(Boolean))}
              testid="provenance-enregistrer"
              disabled={!modifie}
            >
              Enregistrer
            </BoutonSombre>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }} data-testid="provenance-repartition">
          <Eyebrow>Répartition des réponses</Eyebrow>
          {total === 0 ? (
            <p className="text-xs text-[var(--muted)] mt-3">
              Aucune réponse pour l'instant : la question n'est pas encore sur les formulaires du site.
              Le tableau se remplira tout seul dès qu'elle sera posée.
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {repartition.map((r) => (
                <li key={r.source}>
                  <div className="flex justify-between text-xs">
                    <span>{r.source}</span>
                    <span className="tabular-nums text-[var(--muted)]">{r.nb} · {Math.round(100 * r.nb / total)} %</span>
                  </div>
                  <div className="h-1.5 rounded-full mt-1" style={{ background: "#E2E8F0" }}>
                    <div className="h-1.5 rounded-full" style={{ width: `${100 * r.nb / total}%`, background: "var(--primary)" }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Section>
  );
}
